import { IonButton, IonPage, useIonToast, useIonViewWillEnter } from '@ionic/react';
import { ThunkDispatch } from '@reduxjs/toolkit';
import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import { RootState } from '../App';
import { EmailCard } from '../components/EmailCard';
import { Input } from '../components/Forms/Input';
import { QuirkIonContent } from '../components/QuirkIonContent';
import { SiteLogoBanner } from '../components/SiteLogoBanner';
import { useLoggedIn } from '../hooks/useLoggedIn';
import { useLoading } from '../hooks/useQuirkLoading';
import { LoginState, executeEmailLogin, requestEmailLogin, setEmail, setEmailLogin } from '../state/loginSlice';
import { removeAnonEmail } from '../util/removeAnonEmail';
// eslint-disable-next-line css-modules/no-unused-class
import onboardingStyles from '../styles/onboarding.module.css';
import styles from './EmailLogin.module.css';

export const EmailLogin: React.FC = () => {
	const token = useParams<{ token: string }>().token;
	const state: LoginState = useSelector<RootState, LoginState>(state => state.login);
	useLoading({ isLoading: state.emailLogin === 'sending', message: 'Sending email...' });
	const loggedIn = useLoggedIn();
	const dispatch = useDispatch<ThunkDispatch<any, any, any>>();

	const [toast] = useIonToast();
	const sendErrorToast = useCallback(() => {
		toast({
			message: 'Login Attempt failed, perhaps the email has expired, please request another email.',
			duration: 3000,
			position: 'top',
			color: 'danger'
		});
	}, [toast]);
	const history = useHistory();
	const emailLoginAction = () => {
		const email = state.user.email;
		if (email && email?.length > 3) {
			dispatch(requestEmailLogin({ email }));
		} else {
			toast({ message: 'Please provide a valid email', duration: 1500, position: 'top', color: 'danger' });
		}
	};

	useIonViewWillEnter(() => {
		dispatch(setEmailLogin(null));
		dispatch(setEmail(''));
	});

	useEffect(() => {
		async function sendRequest() {
			if (token && token !== 'request') {
				try {
					const result = await dispatch(executeEmailLogin({ token }));
					if ('error' in result) {
						sendErrorToast();
					}
				} catch (e) {
					sendErrorToast();
				}
			}
		}
		sendRequest();
	}, [token, dispatch, sendErrorToast]);
	if (loggedIn.realUser) {
		window.location.href = '/main/latest';
	}

	return (
		<IonPage>
			<QuirkIonContent flush>
				<div className="login-container">
					<SiteLogoBanner siteName={true} />
					<div className="space20" />
					{state.emailLogin === null && (
						<div className={onboardingStyles.contentWrapper}>
							<div className={styles.formContainer}>
								<Input
									type="email"
									placeholder="email"
									value={removeAnonEmail(state.user.email)}
									onChange={event => {
										dispatch(setEmail(event.target.value));
									}}
									testId="emailLoginEmailInput"
								/>
								<IonButton
									size="large"
									expand="block"
									onClick={emailLoginAction}
									data-testid="emailLoginSendEmailButton"
								>
									Send Login Email
								</IonButton>
							</div>
						</div>
					)}

					{state.emailLogin === 'sent' && (
						<EmailCard
							header={`An email has been sent to ${state.user.email}`}
							message="Please click the link in the email to login, the link expires in 5 minutes."
						/>
					)}
					<hr />
					<div className={onboardingStyles.panelRow}>
						<IonButton
							color="primary"
							fill="clear"
							size="default"
							className="radius8"
							onClick={() => history.push('/main/settings/login')}
							expand="block"
						>
							Go back to sign in
						</IonButton>
					</div>
				</div>
			</QuirkIonContent>
		</IonPage>
	);
};
